import { useContext, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { FontAwesomeIcon } from '@/components/Common/FontAwesomeIcon/FontAwesomeIcon';
import { PriceDetails } from '@/components/PriceDetails/PriceDetails';
import { Divider } from '@/components/Common/Divider';
import { useTranslate } from '@/hooks/useTranslate';
import { formatTotalPrice } from '@/utils/pricing';
import { MixpanelEvents } from '@/constants/mixpanelEvents';
import { getMixpanelProperties } from '@/utils/mixpanelProperties';
import { mixpanelService } from '@/services/mixpanelService';
import { DETAILS_STYLES } from '@/constants/styles';
import { cn } from '@/utils/cn';

/**
 * Compact gray order summary used by the EMBEDDED_VIEW template in place of
 * DEFAULT's `TopWrapper` (publisher hero + offer header + products list).
 *
 * Collapsed it shows a single bold Total row; expanded it reveals the
 * Subtotal / Tax breakdown under a hairline. There is deliberately no
 * products list, no promo field and no publisher hero — EMBEDDED_VIEW puts the
 * price up top and hands the rest of the screen to the payment column.
 *
 * The card owns its own chevron rather than reusing `ItemsDetails` (the
 * shared cart-icon + "Details" toggle): here the WHOLE Total row is the
 * affordance, which is why `showItemsToggle` is false for this template.
 *
 * Initial state comes from `template.features.defaultDetailsOpen` so the
 * open/closed default stays configuration-driven.
 */
export const TotalSummaryCard = () => {
  const context = useContext(CheckoutContext);
  const { pricing, template } = context;
  const { t } = useTranslate();
  const [expanded, setExpanded] = useState(template.features.defaultDetailsOpen);

  const handleToggle = () => {
    const nextExpanded = !expanded;
    setExpanded(nextExpanded);
    const properties = getMixpanelProperties(context);
    mixpanelService.track(MixpanelEvents.CHECKOUT_SECTION_STATE_CHANGED, {
      ...(properties as Record<string, unknown>),
      type: nextExpanded ? 'expanded' : 'collapsed',
    });
  };

  return (
    <div
      data-testid="total-summary-card"
      className="w-full rounded-xl bg-muted-extra-light px-4 py-3 text-card-foreground"
    >
      <button
        type="button"
        data-testid="total-summary-toggle"
        data-state={expanded ? 'expanded' : 'collapsed'}
        aria-expanded={expanded}
        onClick={handleToggle}
        className="flex w-full cursor-pointer items-center justify-between gap-2 text-base font-bold"
      >
        <span>{t('total')}</span>
        <span className="flex items-center gap-2">
          <span data-testid="price-total">{formatTotalPrice(pricing)}</span>
          <FontAwesomeIcon
            icon="fa-regular fa-chevron-down"
            className={cn('transition-transform duration-300 ease-in-out', expanded ? 'rotate-180' : 'rotate-0')}
          />
        </span>
      </button>
      {/* Collapse gotcha (same as `Details`): the `grid-rows-[1fr→0fr]` shell
          clamps its `overflow-hidden` child to 0 height, but a border-box with
          padding can't shrink below that padding. Keep the clip wrapper
          padding-free and put the open-state spacing on the INNER child so it
          clips to zero and leaves no phantom gap under the collapsed Total. */}
      <div
        className={cn(
          DETAILS_STYLES.collapsibleBase,
          expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="flex flex-col gap-3 pt-3">
            <Divider />
            <PriceDetails />
          </div>
        </div>
      </div>
    </div>
  );
};
